'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select/select';
import { CountrySelect } from '@/components/ui/country-select/country-select';
import {
  GooglePlacesAutocomplete,
  type PlaceLocation,
} from '@/components/ui/google-places/google-places-autocomplete';
import { useStates } from '@/lib/queries/lookups';
import { parseGooglePlaceAddress } from '@/tenants/_shared/lib/address-validation';

const TAX_NUMBER_COUNTRIES = new Set(['SE', 'ES', 'DK']);

interface Address {
  streetAddress: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface Country {
  id?: string;
  code: string;
  name: string;
}

interface DonorAddressFieldsProps {
  address: Address;
  taxNumber: string;
  countries: Country[];
  googlePlacesApiKey?: string | null;
  showTaxNumber?: boolean;
  requiredFields?: Array<keyof Address>;
  onAddressChange: <K extends keyof Address>(field: K, value: Address[K]) => void;
  onTaxNumberChange: (value: string) => void;
  onCountryChange: (code: string) => void;
}

export function DonorAddressFields({
  address,
  taxNumber,
  countries,
  googlePlacesApiKey,
  showTaxNumber = true,
  requiredFields = [],
  onAddressChange,
  onTaxNumberChange,
  onCountryChange,
}: DonorAddressFieldsProps) {
  const tDonate = useTranslations('donate');
  const tForm = useTranslations('donate.form');
  const tCommon = useTranslations('common');
  const [showManualEntry, setShowManualEntry] = useState(
    !googlePlacesApiKey ||
      !!address.addressLine2 ||
      !!address.city ||
      !!address.state ||
      !!address.postalCode,
  );

  const inputClassName =
    'h-9 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm';

  const selectedCountry = useMemo(
    () => countries.find((c) => c.code === address.country),
    [countries, address.country],
  );
  const { data: stateLookups } = useStates(selectedCountry?.id);
  const hasStateLookups = (stateLookups?.length ?? 0) > 0;

  const searchContext = useMemo(
    () => [address.city, address.state, address.country].filter(Boolean).join(', '),
    [address.city, address.country, address.state],
  );
  const requiresTaxNumber = TAX_NUMBER_COUNTRIES.has(address.country);
  const requiredFieldSet = useMemo(() => new Set(requiredFields), [requiredFields]);
  const shouldForceManualEntryOpen =
    requiredFieldSet.has('city') ||
    requiredFieldSet.has('state') ||
    requiredFieldSet.has('postalCode');
  const hasManualEntryData =
    !!address.addressLine2 || !!address.city || !!address.state || !!address.postalCode;
  const isManualEntryVisible = showManualEntry || shouldForceManualEntryOpen || hasManualEntryData;

  const withRequiredFieldStyling = (field: keyof Address, className = inputClassName) =>
    requiredFieldSet.has(field)
      ? `${className} border-red-500 focus-visible:ring-red-500`
      : className;

  const renderRequiredFieldMessage = (field: keyof Address) =>
    requiredFieldSet.has(field) ? (
      <p className="text-xs text-red-400">{tCommon('required')}</p>
    ) : null;

  const handlePlaceSelect = (place: PlaceLocation) => {
    const parsedAddress = parseGooglePlaceAddress(place, address);

    if (parsedAddress.country && parsedAddress.country !== address.country) {
      onCountryChange(parsedAddress.country);
    }

    onAddressChange('streetAddress', parsedAddress.streetAddress);
    onAddressChange('city', parsedAddress.city);
    onAddressChange('postalCode', parsedAddress.postalCode);
    onAddressChange('country', parsedAddress.country);

    if (hasStateLookups && stateLookups) {
      const normalizedState = parsedAddress.state.trim().toLowerCase();
      const match = stateLookups.find(
        (s) =>
          s.name.trim().toLowerCase() === normalizedState ||
          s.code?.trim().toLowerCase() === normalizedState,
      );
      onAddressChange('state', match?.name ?? parsedAddress.state);
    } else {
      onAddressChange('state', parsedAddress.state);
    }

    setShowManualEntry(true);
  };

  return (
    <div className="space-y-1.5">
      <Label className="text-stone-300 text-sm">{tForm('address')}</Label>

      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-stone-300 text-sm">{tForm('country')}</Label>
          <CountrySelect
            countries={countries}
            value={address.country}
            onValueChange={onCountryChange}
            placeholder={tForm('countryPlaceholder')}
            searchPlaceholder={tForm('countrySearchPlaceholder')}
            className={withRequiredFieldStyling(
              'country',
              'h-10 bg-stone-800 border-stone-700 text-white text-sm [&_input]:text-white',
            )}
            popoverClassName="bg-stone-900 border-stone-700 text-white [&_[cmdk-group]]:text-white [&_[cmdk-item][data-selected='true']]:bg-stone-700 [&_[cmdk-item][data-selected='true']]:text-white"
          />
          {renderRequiredFieldMessage('country')}
        </div>

        <div className="space-y-1.5">
          <Label className="text-stone-300 text-sm">{tForm('streetAddress')}</Label>
          <GooglePlacesAutocomplete
            value={address.streetAddress}
            onInputChange={(value) => onAddressChange('streetAddress', value)}
            onSelect={handlePlaceSelect}
            placeholder={tForm('addressPlaceholder')}
            apiKey={googlePlacesApiKey ?? undefined}
            types={['address']}
            searchContext={searchContext}
            showConfigurationErrors={isManualEntryVisible}
            autoComplete="off"
            enterKeyHint="search"
            autoCapitalize="words"
            inputClassName={withRequiredFieldStyling(
              'streetAddress',
              'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm',
            )}
            dropdownClassName="bg-stone-900 border-stone-700 text-white [&_button]:hover:bg-stone-800"
          />
          {renderRequiredFieldMessage('streetAddress')}
        </div>

        {!shouldForceManualEntryOpen ? (
          <div className="flex justify-start">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 w-full justify-center border-dashed border-stone-700 bg-transparent text-stone-300 hover:border-stone-600 hover:bg-stone-800 hover:text-white sm:w-auto sm:justify-start"
              onClick={() => setShowManualEntry((current) => !current)}
            >
              {isManualEntryVisible ? (
                <>
                  <ChevronUp className="mr-1 h-4 w-4" />
                  {tForm('hideManualEntry')}
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-4 w-4" />
                  {tForm('enterManually')}
                </>
              )}
            </Button>
          </div>
        ) : null}

        {/* Always rendered so Google Places can fill fields; CSS-hidden when collapsed */}
        <div
          className={
            isManualEntryVisible
              ? 'space-y-3 sm:rounded-lg sm:border sm:border-stone-800 sm:bg-stone-900/60 sm:p-3'
              : 'h-0 overflow-hidden'
          }
          aria-hidden={!isManualEntryVisible}
        >
          <div className="space-y-1.5">
            <Label className="text-stone-300 text-sm">{tForm('addressLine2')}</Label>
            <Input
              value={address.addressLine2}
              onChange={(e) => onAddressChange('addressLine2', e.target.value)}
              className="h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm"
              placeholder={tForm('addressLine2Placeholder')}
              aria-label={tForm('addressLine2')}
              autoComplete="off"
              tabIndex={isManualEntryVisible ? 0 : -1}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-stone-300 text-sm">{tForm('city')}</Label>
            <Input
              value={address.city}
              onChange={(e) => onAddressChange('city', e.target.value)}
              className={withRequiredFieldStyling(
                'city',
                'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm',
              )}
              placeholder={tForm('cityPlaceholder')}
              aria-label={tForm('city')}
              autoComplete="off"
              tabIndex={isManualEntryVisible ? 0 : -1}
            />
            {renderRequiredFieldMessage('city')}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-stone-300 text-sm">{tForm('state')}</Label>
              {hasStateLookups && stateLookups ? (
                <Select
                  value={
                    stateLookups.find((s) => s.name === address.state || s.code === address.state)
                      ?.name ?? ''
                  }
                  onValueChange={(value) => onAddressChange('state', value)}
                >
                  <SelectTrigger
                    className={withRequiredFieldStyling(
                      'state',
                      'h-10 bg-stone-800 border-stone-700 text-white text-sm',
                    )}
                    tabIndex={isManualEntryVisible ? 0 : -1}
                  >
                    <SelectValue placeholder={tForm('statePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 max-h-[200px]">
                    {stateLookups.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.name}
                        className="text-white text-sm focus:bg-stone-700 focus:text-white"
                      >
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={address.state}
                  onChange={(e) => onAddressChange('state', e.target.value)}
                  className={withRequiredFieldStyling(
                    'state',
                    'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm',
                  )}
                  placeholder={tForm('statePlaceholder')}
                  aria-label={tForm('state')}
                  autoComplete="off"
                  tabIndex={isManualEntryVisible ? 0 : -1}
                />
              )}
              {renderRequiredFieldMessage('state')}
            </div>

            <div className="space-y-1.5">
              <Label className="text-stone-300 text-sm">{tForm('postalCode')}</Label>
              <Input
                value={address.postalCode}
                onChange={(e) => onAddressChange('postalCode', e.target.value)}
                className={withRequiredFieldStyling(
                  'postalCode',
                  'h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm',
                )}
                placeholder={tForm('postalPlaceholder')}
                aria-label={tForm('postalCode')}
                autoComplete="off"
                tabIndex={isManualEntryVisible ? 0 : -1}
              />
              {renderRequiredFieldMessage('postalCode')}
            </div>
          </div>
        </div>

        {showTaxNumber && requiresTaxNumber ? (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-stone-300 text-sm">
                {tForm(`taxNumberLabel.${address.country}`)}
              </Label>
              <span className="text-xs text-stone-500">{tDonate('optional')}</span>
            </div>
            <Input
              value={taxNumber}
              onChange={(e) => onTaxNumberChange(e.target.value)}
              className="h-10 bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 text-sm"
              placeholder={tForm(`taxNumberLabel.${address.country}`)}
              aria-label={tForm(`taxNumberLabel.${address.country}`)}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { TAX_NUMBER_COUNTRIES };
export type { Address, DonorAddressFieldsProps };
